// The black hole is ray traced per pixel. Each ray follows a Schwarzschild null geodesic
// (units where the event horizon radius is 1), written as a central acceleration
// a = -1.5 h^2 x / r^5 with h the conserved angular momentum of the ray. Rays that cross
// the equatorial plane between the inner and outer radius pick up light from the disk,
// so the back of the disk is bent over and under the shadow on its own.

export const VERTEX_SHADER = `
attribute vec2 a_position;

void main() {
    gl_Position = vec4(a_position, 0.0, 1.0);
}
`

const FRAGMENT_BODY = `
#ifdef GL_FRAGMENT_PRECISION_HIGH
precision highp float;
#else
precision mediump float;
#endif

uniform vec2 u_resolution;
uniform float u_time;
uniform vec3 u_camPos;
uniform vec3 u_camRight;
uniform vec3 u_camUp;
uniform vec3 u_camForward;
uniform float u_tanHalfFov;
uniform vec2 u_shift;
uniform float u_disk;
uniform float u_ring;
uniform float u_stars;
uniform float u_jet;
uniform float u_exposure;
uniform float u_reveal;
uniform vec2 u_pointer;
uniform float u_pointerPull;
uniform float u_stepScale;
uniform float u_streak;

#define PI 3.14159265359
#define TAU 6.28318530718
#define R_IN 3.0
#define R_OUT 13.5
#define R_START 34.0
#define B_CRIT 2.5980762
#define MAX_STEPS 170

float hash12(vec2 p) {
    vec3 p3 = fract(vec3(p.xyx) * 0.1031);
    p3 += dot(p3, p3.yzx + 33.33);
    return fract((p3.x + p3.y) * p3.z);
}

float hash13(vec3 p3) {
    p3 = fract(p3 * 0.1031);
    p3 += dot(p3, p3.zyx + 31.32);
    return fract((p3.x + p3.y) * p3.z);
}

vec3 hash33(vec3 p3) {
    p3 = fract(p3 * vec3(0.1031, 0.1030, 0.0973));
    p3 += dot(p3, p3.yxz + 33.33);
    return fract((p3.xxy + p3.yxx) * p3.zyx);
}

// Value noise that wraps along x with an integer period, so the disk has no seam
float pnoise(vec2 p, float period) {
    vec2 i = floor(p);
    vec2 f = fract(p);
    vec2 u = f * f * (3.0 - 2.0 * f);
    float x0 = mod(i.x, period);
    float x1 = mod(i.x + 1.0, period);
    float a = hash12(vec2(x0, i.y));
    float b = hash12(vec2(x1, i.y));
    float c = hash12(vec2(x0, i.y + 1.0));
    float d = hash12(vec2(x1, i.y + 1.0));
    return mix(mix(a, b, u.x), mix(c, d, u.x), u.y);
}

// Thin concentric streaks: long in angle, short in radius. Fine octaves fade out
// when a single pixel covers more of the disk than they can resolve.
float streaks(float r, float turn, float footprint) {
    float n = pnoise(vec2(turn * 5.0, r * 2.6), 5.0) * 0.46;
    float w2 = 1.0 - smoothstep(0.22, 0.5, footprint * 6.2);
    float w3 = 1.0 - smoothstep(0.22, 0.5, footprint * 13.5);
    float w4 = 1.0 - smoothstep(0.22, 0.5, footprint * 24.0);
    n += mix(0.5, pnoise(vec2(turn * 11.0, r * 6.2 + 17.0), 11.0), w2) * 0.27;
    n += mix(0.5, pnoise(vec2(turn * 23.0, r * 13.5 + 41.0), 23.0), w3) * 0.17;
    n += mix(0.5, pnoise(vec2(turn * 47.0, r * 24.0 + 73.0), 47.0), w4) * 0.10;
    return n;
}

float temperature(float r) {
    float x = R_IN / r;
    return pow(x, 0.75) * pow(max(1.0 - 0.92 * sqrt(x), 0.0), 0.25) * 1.81;
}

// Brown, deep orange, amber, warm white
vec3 palette(float t) {
    t = clamp(t, 0.0, 1.6);
    vec3 c = mix(vec3(0.016, 0.005, 0.002), vec3(0.17, 0.055, 0.016), smoothstep(0.0, 0.26, t));
    c = mix(c, vec3(0.50, 0.17, 0.045), smoothstep(0.24, 0.46, t));
    c = mix(c, vec3(0.88, 0.40, 0.11), smoothstep(0.44, 0.68, t));
    c = mix(c, vec3(1.0, 0.68, 0.36), smoothstep(0.66, 0.9, t));
    c = mix(c, vec3(1.0, 0.88, 0.70), smoothstep(0.88, 1.12, t));
    c = mix(c, vec3(1.0, 0.97, 0.92), smoothstep(1.1, 1.4, t));
    return c;
}

vec4 sampleDisk(vec3 hit, vec3 rayDir, float footprint) {
    float r = length(hit.xz);

    if (r < R_IN - 0.8 || r > R_OUT + 4.5) {
        return vec4(0.0);
    }

    float phi = atan(hit.z, hit.x);
    float omega = 0.22 * pow(R_IN / r, 1.5);

    // Two flow layers that reset in turn: inner gas laps the outer gas,
    // but the shear never winds up into noise
    float period = 22.0;
    float ph1 = fract(u_time / period);
    float ph2 = fract(u_time / period + 0.5);
    float w1 = 1.0 - abs(2.0 * ph1 - 1.0);
    float w2 = 1.0 - w1;
    float turn1 = (phi - omega * ph1 * period) / TAU;
    float turn2 = (phi - omega * ph2 * period) / TAU + 0.31;

    float s = mix(streaks(r + 7.31, turn2, footprint), streaks(r, turn1, footprint), w1);
    s = clamp(0.5 + (s - 0.5) / sqrt(w1 * w1 + w2 * w2), 0.0, 1.0);

    float fil = pnoise(vec2(turn1 * 29.0, r * 8.0 + 5.0), 29.0) * w1 + pnoise(vec2(turn2 * 29.0, r * 8.0 + 9.0), 29.0) * w2;

    float body = smoothstep(R_IN - 0.25, R_IN + 0.8, r) * (1.0 - smoothstep(R_OUT - 5.0, R_OUT + 1.5, r));
    float threads = smoothstep(0.58, 0.86, fil) * smoothstep(R_IN - 0.9, R_IN, r) * (1.0 - smoothstep(R_OUT - 1.5, R_OUT + 4.5, r));
    float density = max(body * (0.32 + 0.68 * s), threads * 0.55);

    // A slow two armed density wave shearing through the gas
    float ripple = 0.5 + 0.5 * sin(phi * 2.0 - log(r) * 7.0 + u_time * 0.04);
    density *= 0.8 + 0.2 * ripple;

    // During page changes the disk is swallowed from the outside in, and grows back out from the ring
    float revealRadius = mix(R_IN - 1.2, R_OUT + 5.0, u_reveal);
    density *= 1.0 - smoothstep(revealRadius - 2.6, revealRadius, r);

    float flicker = 0.93 + 0.07 * pnoise(vec2(u_time * 0.45, r * 0.9), 4096.0);

    float T = temperature(r);
    vec3 vdir = normalize(vec3(-hit.z, 0.0, hit.x));
    float beta = clamp(sqrt(0.5 / max(r - 1.0, 0.3)), 0.0, 0.72);
    float gamma = 1.0 / sqrt(1.0 - beta * beta);
    float cosT = dot(vdir, -rayDir);
    float delta = 1.0 / (gamma * (1.0 - beta * cosT));
    float g = delta * sqrt(max(1.0 - 1.0 / r, 0.04));
    float intensity = g * g * g * (0.1 + 1.2 * T * T * T) * flicker;

    vec3 emission = palette(T * g) * intensity * density * u_disk * 2.6;
    float alpha = clamp(density * 0.9, 0.0, 0.93);

    return vec4(emission, alpha);
}

float jetGlow(vec3 ro, vec3 rd) {
    float a = rd.x * rd.x + rd.z * rd.z;
    float b = ro.x * rd.x + ro.z * rd.z;
    float s0 = max(-b / max(a, 1e-4), 0.0);
    vec3 pc = ro + rd * s0;
    float rho2 = pc.x * pc.x + pc.z * pc.z;
    float y = abs(pc.y);
    float w = 0.3 + 0.03 * y;
    float along = smoothstep(5.0, 16.0, y) * exp(-y / 60.0);
    float knots = 0.75 + 0.25 * sin(y * 0.35 - u_time * 0.5);
    return exp(-rho2 / (w * w)) * along * knots * w * sqrt(PI / max(a, 0.05));
}

vec3 starField(vec3 d, float footprint) {
    vec3 col = vec3(0.0);

    vec3 p = d * 60.0;
    vec3 id = floor(p);

    if (hash13(id + 0.37) < 0.085) {
        vec3 sp = normalize(id + 0.22 + 0.56 * hash33(id + 1.73)) * 60.0;

        if (all(equal(floor(sp), id))) {
            float dist = length(p - sp);
            float size = 0.055;
            float sigma = max(size, footprint * 60.0 * 0.7);
            float k = (size * size) / (sigma * sigma);
            float bright = 0.18 + 2.4 * pow(hash12(id.xy + id.z * 7.13), 9.0);
            vec3 tint = mix(vec3(1.0, 0.86, 0.7), vec3(0.8, 0.88, 1.0), hash12(id.zx + 3.1));
            col += tint * bright * k * exp(-dist * dist / (sigma * sigma));
        }
    }

    vec3 q = d * 150.0;
    vec3 qid = floor(q);

    if (hash13(qid + 5.1) < 0.05) {
        vec3 sq = normalize(qid + 0.22 + 0.56 * hash33(qid + 9.2)) * 150.0;

        if (all(equal(floor(sq), qid))) {
            float dist = length(q - sq);
            float size = 0.07;
            float sigma = max(size, footprint * 150.0 * 0.7);
            float k = (size * size) / (sigma * sigma);
            col += vec3(0.85, 0.82, 0.78) * 0.12 * k * exp(-dist * dist / (sigma * sigma));
        }
    }

    // The faintest, finest layer: dust of stars that only resolves on sharp screens
    vec3 w = d * 320.0;
    vec3 wid = floor(w);

    if (hash13(wid + 2.7) < 0.035) {
        vec3 sw = normalize(wid + 0.22 + 0.56 * hash33(wid + 4.4)) * 320.0;

        if (all(equal(floor(sw), wid))) {
            float dist = length(w - sw);
            float size = 0.08;
            float sigma = max(size, footprint * 320.0 * 0.7);
            float k = (size * size) / (sigma * sigma);
            col += vec3(0.9, 0.86, 0.8) * 0.07 * k * exp(-dist * dist / (sigma * sigma));
        }
    }

    return col;
}

// A faint band of unresolved stars crossed by dust lanes, tilted against the disk.
// The hole bends it into arcs around the shadow, which is how the lensing shows far from the disk.
vec3 galaxy(vec3 d) {
    vec3 n = normalize(vec3(0.34, 0.83, -0.44));
    vec3 t = normalize(cross(n, vec3(0.0, 0.0, 1.0)));
    vec3 b = cross(n, t);
    float lat = dot(d, n);
    float turn = atan(dot(d, b), dot(d, t)) / TAU;
    float band = exp(-lat * lat * 16.0);

    if (band < 0.002) {
        return vec3(0.0);
    }

    float clouds = pnoise(vec2(turn * 24.0, lat * 14.0 + 3.0), 24.0) * 0.6 + pnoise(vec2(turn * 61.0, lat * 37.0 + 11.0), 61.0) * 0.4;
    float lane = smoothstep(0.42, 0.78, pnoise(vec2(turn * 13.0, lat * 22.0 + 7.0), 13.0)) * exp(-lat * lat * 70.0);
    float glow = band * (0.3 + 0.7 * clouds) * (1.0 - 0.8 * lane);

    return vec3(1.0, 0.9, 0.78) * glow * 0.0045;
}

// Deflection collected along a straight line far from the hole, matching the integrator's
// own weak field limit: integral of 1.5 b^3 / (b^2 + s^2)^(5/2) ds
float deflectionF(float s, float b) {
    float q = b * b + s * s;
    return s * (2.0 * s * s + 3.0 * b * b) / (2.0 * b * q * sqrt(q));
}

vec3 bendSegment(vec3 o, vec3 d, float len) {
    vec3 c = o - dot(o, d) * d;
    float b = max(length(c), 1e-3);
    float s1 = dot(o, d);
    float da = deflectionF(s1 + len, b) - deflectionF(s1, b);
    return normalize(d - (c / b) * da);
}

vec3 bendToInfinity(vec3 o, vec3 d) {
    vec3 c = o - dot(o, d) * d;
    float b = max(length(c), 1e-3);
    float da = 1.0 / b - deflectionF(dot(o, d), b);
    return normalize(d - (c / b) * da);
}

// Screen position of a point in space, in the same units as p
vec2 toScreen(vec3 point) {
    vec3 d = point - u_camPos;
    float depth = max(dot(d, u_camForward), 0.05);
    return vec2(dot(d, u_camRight), dot(d, u_camUp)) / (depth * u_tanHalfFov);
}

// Seen almost edge on, the bright front of the disk is drawn out sideways into long soft
// lines of gold, the way an anamorphic cinema lens renders a burning band of light.
// The approaching side carries more of it. Nothing of it reaches into the shadow.
vec3 diskStreaks(vec2 p, float visible) {
    float edgeOn = 1.0 - smoothstep(0.05, 0.3, abs(u_camForward.y));

    if (edgeOn < 0.001 || visible < 0.001) {
        return vec3(0.0);
    }

    vec3 axis = vec3(u_camForward.z, 0.0, -u_camForward.x);
    axis /= max(length(axis), 1e-4);

    vec3 toCam = vec3(u_camPos.x, 0.0, u_camPos.z);
    toCam /= max(length(toCam), 1e-4);

    vec2 a = toScreen(toCam * 6.5 - axis * 9.0);
    vec2 b = toScreen(toCam * 6.5 + axis * 9.0);
    vec2 s = b - a;
    float len = max(length(s), 1e-4);
    s /= len;
    vec2 n = vec2(-s.y, s.x);
    vec2 mid = (a + b) * 0.5;

    float along = dot(p - mid, s);
    float across = dot(p - mid, n);

    // Gas on the +axis side moves towards the camera when this is positive
    float approach = sign(dot(vec3(-axis.z, 0.0, axis.x), u_camPos));
    float side = 1.0 + 0.6 * approach * clamp(along / (len * 0.5), -1.0, 1.0);

    float reach = exp(-abs(along) / (len * 0.75 + 0.2));
    float centre = exp(-along * along / (len * len * 0.06 + 0.01));
    float core = exp(-across * across / 0.00003) * (0.08 * reach + 0.22 * centre);
    float soft = exp(-across * across / 0.0009) * (0.02 * reach + 0.04 * centre);
    float wide = exp(-across * across / 0.012) * 0.004 * reach;

    float flicker = 0.94 + 0.06 * sin(u_time * 0.7 + along * 3.0);
    vec3 gold = mix(vec3(1.0, 0.6, 0.26), vec3(1.0, 0.86, 0.66), clamp(core * 6.0, 0.0, 1.0));

    return gold * (core + soft + wide) * side * flicker * edgeOn * visible;
}

void main() {
    vec2 frag = gl_FragCoord.xy;
    vec2 p = (2.0 * frag - u_resolution) / u_resolution.y;
    vec2 screen = p;

    // The cursor carries a little mass of its own
    vec2 dm = p - u_pointer;
    float pr2 = dot(dm, dm);
    p += dm * (u_pointerPull * 0.0016 / (pr2 + 0.0036)) * exp(-pr2 * 9.0);

    p -= u_shift;

    vec3 dir0 = normalize(u_camForward + (p.x * u_camRight + p.y * u_camUp) * u_tanHalfFov);
    vec3 pos = u_camPos;
    vec3 dir = dir0;
    float r0 = length(pos);

    // Effective impact parameter of the ray, which locates the photon ring exactly
    vec3 hv = cross(pos, dir);
    float h2 = max(dot(hv, hv), 1e-8);
    float bEff = inversesqrt(max(1.0 / h2 - 1.0 / (r0 * r0 * r0), 1e-8));

#ifdef HAS_DERIVATIVES
    float bw = max(fwidth(bEff), 1e-5);
#else
    float bw = 2.0 * u_tanHalfFov / u_resolution.y * r0;
#endif

    float inward = step(dot(pos, dir), 0.0);
    float jet = jetGlow(pos, dir);

    vec3 color = vec3(0.0);
    float alpha = 0.0;
    float escaped = 0.0;
    vec3 outDir = dir;
    bool tracing = true;

    if (r0 > R_START) {
        float pd = dot(pos, dir);
        float disc = pd * pd - (r0 * r0 - R_START * R_START);

        if (pd >= 0.0 || disc <= 0.0) {
            tracing = false;
            escaped = 1.0;
            outDir = bendToInfinity(pos, dir);
        }
        else {
            float t = -pd - sqrt(disc);
            dir = bendSegment(pos, dir, t);
            pos += dir0 * t;
        }
    }

    vec3 vel = dir;
    vec3 hh = cross(pos, vel);
    float hsq = dot(hh, hh);
    float diskFootprint = 2.0 * u_tanHalfFov / u_resolution.y * r0;

    if (tracing) {
        for (int i = 0; i < MAX_STEPS; i++) {
            float r = length(pos);
            float dt = u_stepScale * clamp(0.075 * r + 0.035, 0.05, 2.8);
            vec3 prev = pos;
            float r2 = r * r;

            vel -= 1.5 * hsq * pos / (r2 * r2 * r) * (dt * 0.5);
            pos += vel * dt;

            float rn = length(pos);
            float rn2 = rn * rn;

            vel -= 1.5 * hsq * pos / (rn2 * rn2 * rn) * (dt * 0.5);

            if (prev.y * pos.y < 0.0) {
                float t = prev.y / (prev.y - pos.y);
                vec4 disk = sampleDisk(mix(prev, pos, t), normalize(vel), diskFootprint);
                color += (1.0 - alpha) * disk.rgb;
                alpha += (1.0 - alpha) * disk.a;
            }

            if (rn < 1.02 || alpha > 0.985) {
                break;
            }

            if (rn > R_START + 1.0 && dot(pos, vel) > 0.0) {
                escaped = 1.0;
                outDir = bendToInfinity(pos, normalize(vel));
                break;
            }
        }
    }

#ifdef HAS_DERIVATIVES
    float footprint = max(length(fwidth(outDir)), 1e-5);
#else
    float footprint = 2.0 * u_tanHalfFov / u_resolution.y;
#endif

    vec3 skyDir = normalize(outDir);
    vec3 sky = (starField(skyDir, footprint) + galaxy(skyDir)) * u_stars * mix(0.15, 1.0, u_reveal);
    sky += vec3(0.55, 0.72, 1.0) * jet * u_jet * 0.035 * u_reveal;
    sky += vec3(0.0012, 0.00115, 0.0011);
    color += (1.0 - alpha) * sky * escaped;

    // The photon ring: razor thin and white hot at its core, warmer at its edges,
    // brighter on the approaching side
    float dr = (bEff - (B_CRIT + bw * 0.7)) / (bw * 1.25 + 0.0008);
    float ring = exp(-dr * dr);
    float core = exp(-dr * dr * 3.5);
    float outside = max(bEff - B_CRIT, 0.0);
    float halo = exp(-outside * 7.0) * smoothstep(0.0, bw * 2.0 + 1e-4, bEff - B_CRIT) * 0.05;
    vec3 L = cross(u_camPos, dir0);
    float side = L.y / max(length(L), 1e-4);
    float ringLight = (ring * 1.5 + halo) * (1.0 + 0.55 * side) * u_ring * inward;
    vec3 ringColor = mix(vec3(1.0, 0.86, 0.68), vec3(1.0, 0.97, 0.93), core);
    color += ringColor * ringLight * (1.0 - alpha * 0.55) * mix(1.3, 1.0, u_reveal);

    if (u_streak > 0.001) {
        color += diskStreaks(p, max(escaped, alpha)) * u_streak * u_disk * u_reveal * u_reveal;
    }

    color *= u_exposure;
    color = vec3(1.0) - exp(-color * 1.15);

    float vignette = 1.0 - smoothstep(0.55, 2.2, length(screen * vec2(0.62, 1.0)));
    color *= mix(0.72, 1.0, vignette);
    color = pow(max(color, vec3(0.0)), vec3(0.4545));
    color += (hash12(frag + fract(u_time * 0.37) * 61.0) - 0.5) / 255.0;

    gl_FragColor = vec4(color, 1.0);
}
`

export const buildFragmentShader = (derivatives: boolean): string => {
    const header = derivatives ? '#extension GL_OES_standard_derivatives : enable\n#define HAS_DERIVATIVES 1\n' : ''

    return `${header}${FRAGMENT_BODY}`
}
